import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const userCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'User',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.users.`,
});

export async function getUsers(forceRefresh = false) {
	if (!forceRefresh) {
		const memCached = userCache.getFromMemory('users');
		if (memCached) return memCached;

		try {
			const legacyData = await iframeDataBridge.getStorageAsync('USERS');
			if (legacyData) {
				log('info', 'User', '📦 Seeding Vue cache from legacy storage (USERS)');
				await userCache.put('users', legacyData);
				userCache.setInMemory('users', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'User', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	return userCache.fetchOrCache('users', fetchUsersFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getUsersInfo(forceRefresh = false) {
	return userCache.fetchOrCache('usersInfo', fetchUsersInfoFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

async function fetchUsersFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Core/Users'), { method: 'GET' }, 'token');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const users = await response.json();
	log('success', 'User', `✅ Fetched ${users.length} users`);
	return users;
}

async function fetchUsersInfoFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Core/UsersInfo'), { method: 'GET' }, 'token');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	const usersInfo = await response.json();
	log('success', 'User', `✅ Fetched ${usersInfo.length} users info`);
	return usersInfo;
}

export async function refreshUsers() {
	userCache.clearMemory();
	await userCache.clear();
}

// userTranslator.js toJsonAPI(): a pure mapping copied verbatim, no VM state involved.
function toJsonAPI(user, password) {
	return {
		deliveryLocationHomeId: user.deliveryLocationHomeId,
		customerCode: user.customerCode,
		username: user.userName,
		password: password || '',
		firstName: user.firstName,
		lastName: user.lastName,
		email: user.email,
		locale: user.locale,
		timezone: user.timezone,
	};
}

// nav.js saveUser(): PUT Core/Users/{id}. Its `Token: ctx.user.tokenApp || ctx.user.token` header
// looked security-sensitive, but tokenApp is never assigned anywhere in public/m.eagle (always ''),
// so it's always plain `token` in practice - the same token authenticatedFetch() already sends.
export async function updateUser(user, newPassword) {
	const response = await authenticatedFetch(getApiUrl(`Core/Users/${user.id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(toJsonAPI(user, newPassword)) }, 'token');
	if (response.ok) return { ok: true };
	const body = await response.json().catch(() => null);
	return { ok: false, code: body?.code ?? null };
}
